<template>
  <div class="visualization-content pa-3">
    <v-card outlined elevation="2" class="fact-chart-panel">
      <v-card-title class="visualization-title energy-chart-title">
        <v-icon left color="primary">mdi-lightning-bolt</v-icon>
        Energy Chart
        <v-spacer></v-spacer>
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          size="22"
          width="2"
          color="primary"
        ></v-progress-circular>
      </v-card-title>
      <v-card-text>
        <div class="energy-chart-topbar">
            <div class="fact-chart-control-block">
              <div class="fact-chart-control-label">Year</div>
              <v-btn-toggle v-model="energyYear" mandatory dense color="primary" class="fact-chart-toggle">
                <v-btn v-for="option in energyYearOptions" :key="option.value" :value="option.value" small>
                  {{ option.text }}
                </v-btn>
              </v-btn-toggle>
            </div>
        </div>

        <v-alert v-if="error" type="warning" outlined dense class="mb-4">
          {{ error }}
        </v-alert>

        <div class="energy-chart-grid">
          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ formatEnergySeriesTitle(activeEnergyEfficiencySeries, 'Fuel Efficiency') }}</h4>
                  <source-tooltip-icon
                    :enabled="showParameterSourceTooltips"
                    :text="activeEnergyEfficiencySeries.source_range"
                  />
                </div>
              </div>
              <v-btn-toggle v-model="energyEfficiencyMetric" mandatory dense color="primary" class="fact-price-left-toggle">
                <v-btn value="fuel_efficiency" small>Fuel efficiency</v-btn>
                <v-btn value="cost_of_travel" small>Cost of travel</v-btn>
              </v-btn-toggle>
            </div>
            <div class="energy-chart-canvas-wrap">
              <canvas ref="energyEfficiencyChart"></canvas>
            </div>
          </div>

          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ formatEnergySeriesTitle(activeEnergyIntensitySeries, 'CO2 Intensity by unit of travel') }}</h4>
                  <source-tooltip-icon
                    :enabled="showParameterSourceTooltips"
                    :text="activeEnergyIntensitySeries.source_range"
                  />
                </div>
              </div>
              <v-btn-toggle v-model="energyIntensityMetric" mandatory dense color="primary" class="fact-price-left-toggle">
                <v-btn value="co2_intensity" small>CO2</v-btn>
                <v-btn value="pm25_intensity" small>PM2.5</v-btn>
                <v-btn value="nox_intensity" small>NOx</v-btn>
                <v-btn value="sox_intensity" small>SOx</v-btn>
              </v-btn-toggle>
            </div>
            <div class="energy-chart-canvas-wrap">
              <canvas ref="energyIntensityChart"></canvas>
            </div>
          </div>

          <div class="fact-price-panel energy-chart-wide-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ formatEnergySeriesTitle(activeEnergyPollutionSeries, 'Local Air Pollution Cost by unit of travel') }}</h4>
                  <source-tooltip-icon
                    :enabled="showParameterSourceTooltips"
                    :text="activeEnergyPollutionSeries.source_range"
                  />
                </div>
              </div>
            </div>
            <div class="energy-chart-canvas-wrap">
              <canvas ref="energyPollutionChart"></canvas>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ChartJS from './chartSetup'
import { getPowertrainColor } from './colorPalette'
import SourceTooltipIcon from './SourceTooltipIcon.vue'
import { SHOW_PARAMETER_SOURCE_TOOLTIPS } from './sourceTooltipConfig'

export default {
  name: 'EnergyChartTab',
  components: {
    SourceTooltipIcon
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    analysisResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      energyChartData: null,
      error: '',
      isLoading: false,
        energyYear: '2025',
      energyEfficiencyMetric: 'fuel_efficiency',
      energyIntensityMetric: 'co2_intensity',
      energyEfficiencyChartInstance: null,
      energyIntensityChartInstance: null,
      energyPollutionChartInstance: null
    }
  },
  computed: {
    showParameterSourceTooltips() {
      return SHOW_PARAMETER_SOURCE_TOOLTIPS
    },
    energyYearOptions() {
      if (this.energyChartData && Array.isArray(this.energyChartData.years) && this.energyChartData.years.length) {
        return this.energyChartData.years.map((year) => ({
          text: String(year),
          value: String(year)
        }))
      }

      return [
        { text: '2025', value: '2025' },
        { text: '2030', value: '2030' }
      ]
    },
    activeEnergyEfficiencySeries() {
      return this.getActiveEnergySeries(this.energyEfficiencyMetric)
    },
    activeEnergyIntensitySeries() {
      return this.getActiveEnergySeries(this.energyIntensityMetric)
    },
    activeEnergyPollutionSeries() {
      return this.getActiveEnergySeries('local_air_pollution_cost')
    }
  },
  watch: {
    analysisResult() {
      this.fetchEnergyChartData()
    },
    energyYear() {
      this.$nextTick(this.renderEnergyCharts)
    },
    energyEfficiencyMetric() {
      this.$nextTick(this.renderEnergyCharts)
    },
    energyIntensityMetric() {
      this.$nextTick(this.renderEnergyCharts)
    }
  },
  mounted() {
    this.fetchEnergyChartData()
  },
  beforeDestroy() {
    this.destroyEnergyCharts()
  },
  methods: {
    formatEnergySeriesTitle(series, fallback) {
      const title = (series && series.title) || fallback
      const unit = series && (series.unit || series.y_axis_title)
      if (!unit || String(title).includes(`(${unit})`)) {
        return title
      }

      const titleMatch = String(title).match(/^(.*?)(\s*-\s*\d{4})$/)
      if (titleMatch) {
        return `${titleMatch[1]} (${unit})${titleMatch[2]}`
      }

      return `${title} (${unit})`
    },
    getPreloadedEnergyChartData() {
      return this.analysisResult && this.analysisResult.preloaded_outputs
        ? this.analysisResult.preloaded_outputs.energy_chart || null
        : null
    },
    async fetchEnergyChartData() {
      const requestedFileName = this.analysisResult ? this.analysisResult.file_name : null
      const preloadedData = this.getPreloadedEnergyChartData()
      try {
        this.isLoading = true
        this.error = ''
        if (preloadedData) {
          console.log('[Output Read] Energy Chart:', preloadedData.file_name || requestedFileName || 'preloaded data')
          this.$emit('file-read', {
            section: 'energy_chart',
            sectionTitle: 'Energy Chart',
            fileName: preloadedData.file_name || requestedFileName
            })
            this.energyChartData = preloadedData
            this.syncEnergyYear()
            this.$nextTick(this.renderEnergyCharts)
            return
        }

        this.$emit('loading-state', {
          section: 'energy_chart',
          sectionTitle: 'Energy Chart',
          fileName: requestedFileName,
          isLoading: true
        })

        const response = await fetch(this.domain + '/zev/energy_chart_data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_name: requestedFileName
          })
        })

        const data = await response.json()
        if (!response.ok) {
          throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`)
        }
        if (data.error) throw new Error(data.error)

          this.energyChartData = data
          this.syncEnergyYear()
          console.log('[Output Read] Energy Chart:', data.file_name || requestedFileName || 'latest analysis workbook')
          this.$emit('file-read', {
            section: 'energy_chart',
            sectionTitle: 'Energy Chart',
            fileName: data.file_name || requestedFileName
        })
        this.$nextTick(this.renderEnergyCharts)
      } catch (error) {
        console.error('Error loading Energy Chart data:', error)
        this.error = error.message
        this.energyChartData = null
        this.destroyEnergyCharts()
      } finally {
        this.isLoading = false
        this.$emit('loading-state', {
          section: 'energy_chart',
          sectionTitle: 'Energy Chart',
          fileName: requestedFileName,
          isLoading: false
          })
        }
      },
      syncEnergyYear() {
        const yearValues = this.energyYearOptions.map((option) => option.value)
        if (yearValues.length && !yearValues.includes(this.energyYear)) {
          this.energyYear = yearValues[0]
        }
      },
      getActiveEnergySeries(metric) {
        const emptySeries = { title: '', unit: '', categories: [], datasets: [] }
        if (!this.energyChartData || !this.energyChartData.charts) return emptySeries

      const yearData = this.energyChartData.charts[this.energyYear]
      if (!yearData) return emptySeries

      return yearData[metric] || emptySeries
    },
    destroyEnergyCharts() {
      this.energyEfficiencyChartInstance = this.destroyChart(this.energyEfficiencyChartInstance)
      this.energyIntensityChartInstance = this.destroyChart(this.energyIntensityChartInstance)
      this.energyPollutionChartInstance = this.destroyChart(this.energyPollutionChartInstance)
    },
    destroyChart(chart) {
      if (chart) chart.destroy()
      return null
    },
    destroyChartOnCanvas(canvas) {
      if (!canvas || !ChartJS.getChart) return
      const existingChart = ChartJS.getChart(canvas)
      if (existingChart) existingChart.destroy()
    },
    renderEnergyCharts() {
      this.energyEfficiencyChartInstance = this.renderGroupedBarChart(
        this.$refs.energyEfficiencyChart,
        this.energyEfficiencyChartInstance,
        this.activeEnergyEfficiencySeries
      )
      this.energyIntensityChartInstance = this.renderGroupedBarChart(
        this.$refs.energyIntensityChart,
        this.energyIntensityChartInstance,
        this.activeEnergyIntensitySeries
      )
      this.energyPollutionChartInstance = this.renderGroupedBarChart(
        this.$refs.energyPollutionChart,
        this.energyPollutionChartInstance,
        this.activeEnergyPollutionSeries,
        { legendPosition: 'right' }
      )
    },
    renderGroupedBarChart(canvas, chartInstance, series, chartOptions = {}) {
      if (!canvas) return chartInstance

      if (chartInstance) chartInstance.destroy()
      this.destroyChartOnCanvas(canvas)

      const datasets = series.datasets || []
      if (!datasets.length) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return null
      }

      return new ChartJS(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: series.categories,
          datasets: datasets.map(dataset => ({
            label: dataset.label,
            data: dataset.values,
            backgroundColor: getPowertrainColor(dataset.label),
            borderColor: '#ffffff',
            borderWidth: 1
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: chartOptions.legendPosition || 'bottom' },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${Number(context.parsed.y).toLocaleString()}`
              }
            }
          },
          scales: {
            x: { grid: { display: false } },
            y: {
              beginAtZero: true,
              ticks: { callback: (value) => Number(value).toLocaleString() }
            }
          }
        }
      })
    }
  }
}
</script>
